import { useState } from "react";
import { router } from "@inertiajs/react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Beer, Building2, Check, ChevronLeft, ChevronRight, User } from "lucide-react";

type Invitation = {
  id: number;
  email: string;
  token: string;
  expires_at: string;
};

type Props = {
  invitation: Invitation;
  errors?: Record<string, string[]>;
  flash?: { notice?: string; alert?: string };
};

const STEPS = [
  { label: "Organization", icon: Building2 },
  { label: "Account", icon: User },
  { label: "Confirm", icon: Check },
] as const;

type FormData = {
  organization_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  username: string;
  password: string;
  password_confirmation: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

function validate(step: number, data: FormData): FormErrors {
  const errors: FormErrors = {};

  if (step === 0) {
    if (!data.organization_name.trim()) {
      errors.organization_name = "Organization name is required";
    }
  }

  if (step === 1) {
    if (!data.first_name.trim()) errors.first_name = "First name is required";
    if (!data.last_name.trim()) errors.last_name = "Last name is required";
    if (!data.username.trim()) {
      errors.username = "Username is required";
    } else if (data.username.trim().length < 3) {
      errors.username = "Username must be at least 3 characters";
    }
    if (!data.password) {
      errors.password = "Password is required";
    } else if (data.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }
    if (!data.password_confirmation) {
      errors.password_confirmation = "Please confirm your password";
    } else if (data.password !== data.password_confirmation) {
      errors.password_confirmation = "Passwords do not match";
    }
  }

  return errors;
}

export default function InvitationEdit({ invitation, errors: serverErrors = {}, flash }: Props) {
  useFlashToast(flash);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [clientErrors, setClientErrors] = useState<FormErrors>({});
  const [data, setData] = useState<FormData>({
    organization_name: "",
    first_name: "",
    last_name: "",
    middle_name: "",
    username: "",
    password: "",
    password_confirmation: "",
  });

  function update(field: keyof FormData, value: string) {
    setData((prev) => ({ ...prev, [field]: value }));
    setClientErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function serverError(field: string): string | undefined {
    const msgs = serverErrors[field];
    return msgs?.[0];
  }

  function errorFor(field: keyof FormData): string | undefined {
    return clientErrors[field] || serverError(field);
  }

  function goNext() {
    const errs = validate(step, data);
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      return;
    }
    setClientErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function goBack() {
    setClientErrors({});
    setStep((s) => Math.max(s - 1, 0));
  }

  function handleSubmit() {
    setSubmitting(true);
    router.put(
      `/invitations/${invitation.token}`,
      {
        organization: { name: data.organization_name },
        user: {
          first_name: data.first_name,
          last_name: data.last_name,
          middle_name: data.middle_name,
          username: data.username,
          password: data.password,
          password_confirmation: data.password_confirmation,
        },
      },
      {
        onFinish: () => setSubmitting(false),
      },
    );
  }

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-amber-500/10">
            <Beer className="size-6 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Accept Invitation</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up your organization and account to get started.
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            {STEPS.map((s, i) => (
              <div
                key={s.label}
                className="flex items-center gap-1.5 text-sm"
              >
                <div
                  className={`flex size-7 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                    i < step
                      ? "bg-amber-600 text-white"
                      : i === step
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {i < step ? <Check className="size-3.5" /> : i + 1}
                </div>
                <span
                  className={`hidden sm:inline ${
                    i <= step ? "font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-1.5" aria-label={`Step ${step + 1} of ${STEPS.length}`} />
        </div>

        <Card>
          {step === 0 && (
            <>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
                <CardDescription>
                  Create the organization you'll use to manage your collection.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization name</Label>
                  <Input
                    id="organization_name"
                    type="text"
                    placeholder="e.g. My Home Bar, The Whiskey Club…"
                    value={data.organization_name}
                    onChange={(e) => update("organization_name", e.target.value)}
                    aria-invalid={!!errorFor("organization_name")}
                    autoFocus
                    required
                  />
                  {errorFor("organization_name") && (
                    <p className="text-sm text-destructive">{errorFor("organization_name")}</p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={goNext}>
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </CardFooter>
            </>
          )}

          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle>Your Account</CardTitle>
                <CardDescription>
                  Fill in your details to create your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={invitation.email}
                    disabled
                    className="bg-muted"
                    aria-readonly="true"
                  />
                  <p className="text-xs text-muted-foreground">
                    This is the email from your invitation and cannot be changed.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={data.first_name}
                      onChange={(e) => update("first_name", e.target.value)}
                      aria-invalid={!!errorFor("first_name")}
                      autoFocus
                      required
                    />
                    {errorFor("first_name") && (
                      <p className="text-sm text-destructive">{errorFor("first_name")}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="middle_name">Middle name</Label>
                    <Input
                      id="middle_name"
                      type="text"
                      value={data.middle_name}
                      onChange={(e) => update("middle_name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={data.last_name}
                      onChange={(e) => update("last_name", e.target.value)}
                      aria-invalid={!!errorFor("last_name")}
                      required
                    />
                    {errorFor("last_name") && (
                      <p className="text-sm text-destructive">{errorFor("last_name")}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Choose a username…"
                    value={data.username}
                    onChange={(e) => update("username", e.target.value)}
                    aria-invalid={!!errorFor("username")}
                    required
                  />
                  {errorFor("username") && (
                    <p className="text-sm text-destructive">{errorFor("username")}</p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={data.password}
                      onChange={(e) => update("password", e.target.value)}
                      aria-invalid={!!errorFor("password")}
                      required
                    />
                    {errorFor("password") && (
                      <p className="text-sm text-destructive">{errorFor("password")}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password_confirmation">Confirm password</Label>
                    <Input
                      id="password_confirmation"
                      type="password"
                      value={data.password_confirmation}
                      onChange={(e) => update("password_confirmation", e.target.value)}
                      aria-invalid={!!errorFor("password_confirmation")}
                      required
                    />
                    {errorFor("password_confirmation") && (
                      <p className="text-sm text-destructive">{errorFor("password_confirmation")}</p>
                    )}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={goBack}>
                  <ChevronLeft className="size-4" />
                  Back
                </Button>
                <Button onClick={goNext}>
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </CardFooter>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle>Confirm Details</CardTitle>
                <CardDescription>
                  Review your information before creating your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-3 rounded-md border p-4">
                  <h3 className="text-sm font-semibold text-foreground">Organization</h3>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Name</p>
                    <p className="text-sm">{data.organization_name}</p>
                  </div>
                </div>

                <div className="space-y-3 rounded-md border p-4">
                  <h3 className="text-sm font-semibold text-foreground">Account</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Email</p>
                      <p className="text-sm">{invitation.email}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Username</p>
                      <p className="text-sm">{data.username}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">First name</p>
                      <p className="text-sm">{data.first_name}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Last name</p>
                      <p className="text-sm">{data.last_name}</p>
                    </div>
                    {data.middle_name && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Middle name</p>
                        <p className="text-sm">{data.middle_name}</p>
                      </div>
                    )}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Password</p>
                      <p className="text-sm">••••••••</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={goBack}>
                  <ChevronLeft className="size-4" />
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Creating account…" : "Create Account"}
                </Button>
              </CardFooter>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          This invitation was sent to <span className="font-medium">{invitation.email}</span>
        </p>
      </div>
    </div>
  );
}
